import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { WORD_SETS, termVariables } from '@attendance/shared/terminology';

/* ===========================================================================
 * ARABIC IS A CHOICE ON THE PROFILE PAGE, AND ONLY THERE (owner, 2026-10-14).
 *
 * It was off for a while (English only), and the header carried a globe that
 * swapped language on every press. Now the reader picks it once, in Profile →
 * Preferences, and nothing else selects it behind their back: the detector
 * reads only what that choice stored, never the browser's `Accept-Language`.
 * The key is `language`, not the old `lang`, so a value left by the retired
 * header toggle does not switch anyone to Arabic today.
 * ======================================================================== */
export const SUPPORTED_LOCALES = ['en', 'ar'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Locales that should flip the layout to right-to-left. */
const RTL_LOCALES: ReadonlySet<string> = new Set(['ar', 'he', 'fa', 'ur']);

export function isRtlLocale(lng: string | undefined): boolean {
  if (!lng) return false;
  // Match "ar", "ar-EG", etc. by checking the language subtag only.
  return RTL_LOCALES.has(lng.split('-')[0]!);
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true,
    debug: import.meta.env.DEV,
    defaultNS: 'common',
    ns: ['common'],
    // Filled by `i18nReady` below, not bundled into the entry chunk.
    resources: {},
    partialBundledLanguages: true,
    interpolation: {
      escapeValue: false, // React already escapes
      // The company's words (`{{term.Employees}}`, `{{term.worksite}}`…) — the
      // office set until branding answers; `lib/terminology.ts` swaps them.
      defaultVariables: { term: termVariables(WORD_SETS.hr.en, 'en') },
    },
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'language',
    },
    react: {
      useSuspense: false,
      // `termsChanged`: the company's words changed, so every string re-reads them.
      bindI18n: 'languageChanged termsChanged',
    },
  });

/**
 * EACH CATALOGUE IS ITS OWN CHUNK (2026-10-02 audit, W-01).
 *
 * `locales/en/common.json` is ~370 KB of strings. Imported statically it sat
 * in the entry chunk, parsed before the first paint on every visit, and could
 * never be cached apart from the code around it. It is fetched beside the
 * entry now, and `main.tsx` waits for it before `createRoot` — so no screen
 * ever renders a raw key while it loads. (`useSuspense` is off, so nothing
 * else would wait.) Arabic is fetched only by a reader who chose it, and must
 * be loaded before `changeLanguage` — which is why the profile card awaits
 * `loadLocale` first.
 */
const CATALOGUES: Record<SupportedLocale, () => Promise<{ default: object }>> = {
  en: () => import('../locales/en/common.json'),
  ar: () => import('../locales/ar/common.json'),
};

export async function loadLocale(lng: string): Promise<void> {
  const code = lng.split('-')[0] as SupportedLocale;
  if (!(code in CATALOGUES) || i18n.hasResourceBundle(code, 'common')) return;
  const m = await CATALOGUES[code]();
  i18n.addResourceBundle(code, 'common', m.default, true, true);
}

export const i18nReady: Promise<void> = Promise.all([
  loadLocale('en'),
  loadLocale(i18n.language || 'en'),
]).then(() => undefined);

/**
 * Apply `dir`/`lang` attributes to `<html>` based on the active locale.
 * Wired to an i18next `languageChanged` listener in `main.tsx`.
 */
export function applyDocumentLang(lng: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lng;
  document.documentElement.dir = isRtlLocale(lng) ? 'rtl' : 'ltr';
}

export default i18n;
